import { useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, LockKeyhole, Mail } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { usePatientPortalAuth } from "@/context/PatientPortalAuthContext";
import { PatientPortalAuthLayout } from "@/components/patient-portal/PatientPortalAuthLayout";

export default function PatientPortalLogin() {
  const { patient, login } = usePatientPortalAuth();
  const navigate = useNavigate();
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  if (patient) return <Navigate to="/area-paciente" replace />;

  async function submit(event) {
    event.preventDefault();
    const email = emailRef.current?.value.trim() || "";
    const password = passwordRef.current?.value || "";
    if (!email || !password) return;
    setBusy(true);
    try {
      await login(email, password);
      navigate("/area-paciente", { replace: true });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  }

  return <PatientPortalAuthLayout eyebrow="Acesso seguro" title="Olá, seja bem-vindo." description="Entre com o e-mail cadastrado na clínica e sua senha.">
    <form onSubmit={submit} className="space-y-5" data-testid="patient-portal-login-form">
      <label className="block text-xs font-bold uppercase tracking-[.16em] text-[#52616B]">E-mail
        <div className="relative mt-2"><Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6A7A67]" />
          <input required type="email" autoComplete="email" ref={emailRef} placeholder="seuemail@exemplo.com" data-testid="patient-portal-email-input" className="min-h-12 w-full rounded-md border border-[#DCD7CE] bg-white py-3 pl-11 pr-4 text-sm text-[#0B2230] outline-none transition-colors placeholder:text-[#9AA4A0] focus:border-[#0B2230]" />
        </div>
      </label>
      <label className="block text-xs font-bold uppercase tracking-[.16em] text-[#52616B]">Senha
        <div className="relative mt-2"><LockKeyhole className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6A7A67]" />
          <input required type={showPassword ? "text" : "password"} autoComplete="current-password" ref={passwordRef} placeholder="Digite sua senha" data-testid="patient-portal-password-input" className="min-h-12 w-full rounded-md border border-[#DCD7CE] bg-white py-3 pl-11 pr-12 text-sm text-[#0B2230] outline-none transition-colors placeholder:text-[#9AA4A0] focus:border-[#0B2230]" />
          <button type="button" onClick={() => setShowPassword((value) => !value)} data-testid="patient-portal-toggle-password-button" aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"} className="absolute right-3 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center text-[#52616B] transition-colors hover:text-[#0B2230]">{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
        </div>
      </label>
      <button type="submit" disabled={busy} data-testid="patient-portal-login-submit" className="mt-2 flex min-h-12 w-full items-center justify-center gap-2 rounded-md bg-[#0B2230] px-5 text-xs font-bold uppercase tracking-[.16em] text-white transition-colors hover:bg-[#143346] disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? "Entrando" : "Entrar"}</button>
      <div className="flex items-center justify-between gap-4 pt-1 text-sm"><Link to="/area-paciente/esqueci-senha" data-testid="patient-portal-forgot-password-link" className="font-semibold text-[#1E596E] hover:underline">Esqueci minha senha</Link><span className="text-right text-xs text-[#7A8684]">Primeiro acesso: use seu CPF como senha.</span></div>
    </form>
  </PatientPortalAuthLayout>;
}